import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  RefreshCw,
  Send,
  Inbox,
} from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = 'https://kotak-risk-analysis.alliantprojects.com/api/v1/validation-analysis';

interface ResponseFile {
  fileName: string;
  status: string;
  totalRecords: number;
  success: number;
  failed: number;
  matchedBeneficiaries: number;
  uploadDate: string;
}

interface ReconRow {
  request: {
    batchId: string;
    fileName: string;
    totalRecords: number;
    newBeneficiaries: number;
    retries: number;
    lowRisk: number;
    mediumRisk: number;
    highRisk: number;
    criticalRisk: number;
    averageRiskScore: string;
    status: string;
    uploadDate: string;
    sentDate: string;
  };
  response: {
    responded: number;
    success: number;
    failed: number;
    pending: number;
    successRate: number;
    files: ResponseFile[];
  };
}

export const ReconciliationReport: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<ReconRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/reconciliation-report?page=${page}&limit=15`);
      setData(res.data.data || []);
      setTotalPages(res.data.totalPages || 1);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch reconciliation report', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page]);

  const getStatusBadge = (pending: number, success: number, total: number) => {
    if (pending === total) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
          <Clock className="w-3 h-3" /> Pending
        </span>
      );
    }
    if (pending === 0 && success === total) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3" /> All Success
        </span>
      );
    }
    if (pending === 0) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
          <CheckCircle className="w-3 h-3" /> Responded
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800">
        <Clock className="w-3 h-3" /> Partial
      </span>
    );
  };

  const formatDate = (d: string) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  if (loading && data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mb-4" />
        <p className="text-gray-600">Loading reconciliation report...</p>
      </div>
    );
  }

  // Summary stats
  const totalReq = data.reduce((s, r) => s + r.request.totalRecords, 0);
  const totalResponded = data.reduce((s, r) => s + r.response.responded, 0);
  const totalSuccess = data.reduce((s, r) => s + r.response.success, 0);
  const totalFailed = data.reduce((s, r) => s + r.response.failed, 0);
  const totalPending = data.reduce((s, r) => s + r.response.pending, 0);

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <button onClick={() => navigate('/cggbatches')} className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to Batches
        </button>
        <h1 className="text-3xl font-bold text-gray-900">REQ → RES Reconciliation</h1>
        <p className="text-gray-600 mt-1">Track request files sent to bank and their response status</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
          <p className="text-xs text-gray-500">Total Batches</p>
          <p className="text-2xl font-bold">{total}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
          <div className="flex items-center gap-2">
            <Send className="w-4 h-4 text-purple-500" />
            <p className="text-xs text-gray-500">Requested</p>
          </div>
          <p className="text-2xl font-bold">{totalReq.toLocaleString()}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <p className="text-xs text-gray-500">Success</p>
          </div>
          <p className="text-2xl font-bold text-green-700">{totalSuccess.toLocaleString()}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-red-500">
          <div className="flex items-center gap-2">
            <XCircle className="w-4 h-4 text-red-500" />
            <p className="text-xs text-gray-500">Failed</p>
          </div>
          <p className="text-2xl font-bold text-red-700">{totalFailed.toLocaleString()}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-yellow-500" />
            <p className="text-xs text-gray-500">Pending</p>
          </div>
          <p className="text-2xl font-bold text-yellow-700">{totalPending.toLocaleString()}</p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow-lg rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-8"></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">REQ File</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Sent Date</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Total</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">New</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Retries</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Success</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Failed</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Pending</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Rate</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">RES Files</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.map((row) => {
                const isExpanded = expandedRow === row.request.batchId;
                return (
                  <React.Fragment key={row.request.batchId}>
                    <tr
                      className={`hover:bg-gray-50 cursor-pointer ${isExpanded ? 'bg-blue-50' : ''}`}
                      onClick={() => setExpandedRow(isExpanded ? null : row.request.batchId)}
                    >
                      <td className="px-4 py-3">
                        {row.response.files.length > 0 ? (
                          isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />
                        ) : <span className="w-4 h-4 block" />}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-xs" title={row.request.fileName}>
                          {row.request.fileName}
                        </div>
                        <div className="text-xs text-gray-400">{row.request.batchId}</div>
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-gray-700">{formatDate(row.request.sentDate || row.request.uploadDate)}</td>
                      <td className="px-4 py-3 text-center text-sm font-semibold">{row.request.totalRecords.toLocaleString()}</td>
                      <td className="px-4 py-3 text-center text-sm text-blue-700">{(row.request.newBeneficiaries || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-center text-sm text-orange-700">{(row.request.retries || 0) > 0 ? (row.request.retries || 0).toLocaleString() : '-'}</td>
                      <td className="px-4 py-3 text-center text-sm font-semibold text-green-700">{row.response.success.toLocaleString()}</td>
                      <td className="px-4 py-3 text-center text-sm font-semibold text-red-700">{row.response.failed > 0 ? row.response.failed.toLocaleString() : '-'}</td>
                      <td className="px-4 py-3 text-center text-sm font-semibold text-yellow-700">{row.response.pending > 0 ? row.response.pending.toLocaleString() : '-'}</td>
                      <td className="px-4 py-3 text-center">
                        {row.response.responded > 0 ? (
                          <span className={`text-sm font-bold ${row.response.successRate >= 95 ? 'text-green-700' : row.response.successRate >= 80 ? 'text-yellow-700' : 'text-red-700'}`}>
                            {row.response.successRate}%
                          </span>
                        ) : <span className="text-sm text-gray-400">-</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getStatusBadge(row.response.pending, row.response.success, row.request.totalRecords)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                          <Inbox className="w-3 h-3" /> {row.response.files.length}
                        </span>
                      </td>
                    </tr>

                    {/* Expanded: Response Files */}
                    {isExpanded && row.response.files.length > 0 && (
                      <tr>
                        <td colSpan={12} className="px-0 py-0">
                          <div className="bg-gray-50 border-t border-b border-gray-200 px-8 py-4">
                            <div className="flex items-center gap-2 mb-3">
                              <Inbox className="w-4 h-4 text-blue-600" />
                              <h4 className="text-sm font-semibold text-gray-700">Response Files ({row.response.files.length})</h4>
                            </div>

                            {/* Batch composition */}
                            <div className="flex gap-4 mb-3 text-xs">
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">New: {row.request.newBeneficiaries || 0}</span>
                              <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded">Retries: {row.request.retries || 0}</span>
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded">Low: {row.request.lowRisk} | Med: {row.request.mediumRisk} | High: {row.request.highRisk}{row.request.criticalRisk > 0 ? ` | Critical: ${row.request.criticalRisk}` : ''}</span>
                            </div>

                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-xs text-gray-500 uppercase">
                                  <th className="text-left py-2">Response File</th>
                                  <th className="text-center py-2">Date</th>
                                  <th className="text-center py-2">Total</th>
                                  <th className="text-center py-2">Matched</th>
                                  <th className="text-center py-2">Success</th>
                                  <th className="text-center py-2">Failed</th>
                                  <th className="text-center py-2">Status</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {row.response.files.map((f, i) => (
                                  <tr key={i} className="hover:bg-white">
                                    <td className="py-2 text-gray-700 truncate max-w-sm" title={f.fileName}>
                                      <FileText className="w-3 h-3 inline mr-1 text-gray-400" />
                                      {f.fileName}
                                    </td>
                                    <td className="text-center py-2 text-gray-600">{formatDate(f.uploadDate)}</td>
                                    <td className="text-center py-2">{f.totalRecords.toLocaleString()}</td>
                                    <td className="text-center py-2 font-semibold text-blue-700">{f.matchedBeneficiaries}</td>
                                    <td className="text-center py-2 text-green-700">{f.success.toLocaleString()}</td>
                                    <td className="text-center py-2 text-red-700">{f.failed > 0 ? f.failed.toLocaleString() : '-'}</td>
                                    <td className="text-center py-2">
                                      <span className={`px-2 py-0.5 rounded text-xs ${f.status === 'COMPLETED' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                        {f.status}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Page {page} of {totalPages} ({total} batches)
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReconciliationReport;
